import { useState, useEffect } from "react";
import JSZip from "jszip";
import { useGoogleAuth } from "@/hooks/useGoogleAuth";
import { trpc } from "@/lib/trpc";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { AlertTriangle, Search, Upload, Download, FileText, X } from "lucide-react";
import { toast } from "sonner";

const AVISO_LEGAL = [
  "Não pode corrigir dados que alterem valor de imposto, base de cálculo ou alíquota",
  "Não pode corrigir quantidade ou valor da operação",
  "Não pode alterar emitente ou destinatário",
  "Não pode alterar data de emissão ou saída",
];

function AvisoLegal() {
  return (
    <div className="flex gap-3 p-4 rounded-lg border border-yellow-500/30 bg-yellow-500/5 mb-4">
      <AlertTriangle className="h-5 w-5 text-yellow-500 shrink-0 mt-0.5" />
      <div>
        <p className="text-sm font-medium text-yellow-600 mb-1">A CC-e não pode corrigir:</p>
        <ul className="text-sm text-muted-foreground space-y-0.5">
          {AVISO_LEGAL.map((item, i) => (
            <li key={i} className="flex items-start gap-1">
              <span className="mt-1 shrink-0">•</span>
              <span>{item}</span>
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

function StatusBadge({ status }: { status: string }) {
  if (status === "autorizada") return <Badge className="bg-green-500/20 text-green-600 border-green-500/30">Autorizada</Badge>;
  if (status === "rejeitada") return <Badge className="bg-red-500/20 text-red-600 border-red-500/30">Rejeitada</Badge>;
  return <Badge className="bg-yellow-500/20 text-yellow-600 border-yellow-500/30">Erro</Badge>;
}

// ─── Aba 1: Emitir Individual ────────────────────────────────────────────────

function AbaEmitirIndividual({ isAdmin, emitentes }: { isAdmin: boolean; emitentes: any[] }) {
  const [search, setSearch] = useState("");
  const [notaSelecionada, setNotaSelecionada] = useState<any>(null);
  const [xCorrecao, setXCorrecao] = useState("");
  const [resultado, setResultado] = useState<any>(null);

  const { data: nfes = [], isLoading: loadingNfes } = trpc.cce.listarNfes.useQuery(
    { search: search || undefined },
    { enabled: true }
  );

  const utils = trpc.useUtils();

  const emitirMutation = trpc.cce.emitir.useMutation({
    onSuccess: (data) => {
      setResultado(data);
      if (data.success) {
        toast.success(`CC-e emitida com sucesso! Protocolo: ${data.protocolo}`);
        utils.cce.listarNfes.invalidate();
        utils.cce.historico.invalidate();
      } else {
        toast.error(`CC-e rejeitada: ${data.xMotivo}`);
      }
    },
    onError: (err) => {
      toast.error(`Erro ao emitir CC-e: ${err.message}`);
    },
  });

  const handleEmitir = () => {
    if (!notaSelecionada) return;
    if (xCorrecao.trim().length < 15) {
      toast.error("O texto de correção deve ter no mínimo 15 caracteres.");
      return;
    }
    emitirMutation.mutate({ nfeId: notaSelecionada.id, xCorrecao: xCorrecao.trim() });
  };

  return (
    <div className="space-y-4">
      <AvisoLegal />

      {/* Busca de NF-e */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Selecionar NF-e</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por número, chave ou destinatário..."
              value={search}
              onChange={(e) => { setSearch(e.target.value); setNotaSelecionada(null); setResultado(null); }}
              className="pl-9"
            />
          </div>

          {loadingNfes && <p className="text-sm text-muted-foreground">Buscando...</p>}

          {nfes.length > 0 && !notaSelecionada && (
            <div className="border rounded-lg overflow-hidden max-h-64 overflow-y-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">Número</th>
                    <th className="text-left p-3 font-medium">Série</th>
                    <th className="text-left p-3 font-medium">Destinatário</th>
                    <th className="text-left p-3 font-medium">Data Emissão</th>
                    <th className="text-left p-3 font-medium">Emitente</th>
                    <th className="text-left p-3 font-medium">Última CC-e</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {nfes.map((nfe: any) => (
                    <tr key={nfe.id} className="border-b hover:bg-muted/30 cursor-pointer" onClick={() => setNotaSelecionada(nfe)}>
                      <td className="p-3 font-mono">{nfe.numeroNfe}</td>
                      <td className="p-3">{nfe.serie}</td>
                      <td className="p-3">{nfe.destinatarioNome || "-"}</td>
                      <td className="p-3">{nfe.dataEmissao ? new Date(nfe.dataEmissao).toLocaleDateString("pt-BR") : "-"}</td>
                      <td className="p-3 text-xs text-muted-foreground">{nfe.razaoSocialEmitente || "-"}</td>
                      <td className="p-3 text-xs text-muted-foreground">{nfe.cceUltimaSequencia ? `Seq. ${nfe.cceUltimaSequencia}` : "Nenhuma"}</td>
                      <td className="p-3"><Button size="sm" variant="outline">Selecionar</Button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {notaSelecionada && (
            <div className="p-4 border rounded-lg bg-muted/30 flex items-start justify-between gap-4">
              <div className="space-y-1 text-sm">
                <p><span className="text-muted-foreground">NF-e:</span> <span className="font-mono font-medium">Nº {notaSelecionada.numeroNfe} – Série {notaSelecionada.serie}</span></p>
                <p><span className="text-muted-foreground">Emitente:</span> {notaSelecionada.razaoSocialEmitente || "-"}</p>
                <p><span className="text-muted-foreground">Destinatário:</span> {notaSelecionada.destinatarioNome || "-"}</p>
                <p><span className="text-muted-foreground">Chave:</span> <span className="font-mono text-xs">{notaSelecionada.chaveNfe}</span></p>
                <p><span className="text-muted-foreground">Última CC-e:</span> {notaSelecionada.cceUltimaSequencia ? `Sequência ${notaSelecionada.cceUltimaSequencia} em ${notaSelecionada.cceUltimaData ? new Date(notaSelecionada.cceUltimaData).toLocaleDateString("pt-BR") : "-"}` : "Nenhuma emitida"}</p>
              </div>
              <Button size="sm" variant="ghost" onClick={() => { setNotaSelecionada(null); setResultado(null); }}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Texto de correção */}
      {notaSelecionada && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Texto de Correção</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <Textarea
                placeholder="Descreva a correção (mínimo 15 caracteres)..."
                value={xCorrecao}
                onChange={(e) => setXCorrecao(e.target.value)}
                rows={4}
                maxLength={1000}
              />
              <p className={`text-xs mt-1 ${xCorrecao.length < 15 ? "text-red-500" : "text-muted-foreground"}`}>
                {xCorrecao.length}/1000 caracteres {xCorrecao.length < 15 && `(mínimo 15)`}
              </p>
            </div>

            <Button
              onClick={handleEmitir}
              disabled={emitirMutation.isPending || xCorrecao.trim().length < 15}
              className="w-full"
            >
              {emitirMutation.isPending ? "Emitindo..." : "Emitir CC-e"}
            </Button>

            {resultado && (
              <div className={`p-4 rounded-lg border ${resultado.success ? "border-green-500/30 bg-green-500/5" : "border-red-500/30 bg-red-500/5"}`}>
                <div className="flex items-center gap-2 mb-2">
                  <StatusBadge status={resultado.success ? "autorizada" : "rejeitada"} />
                  <span className="text-sm font-mono">cStat: {resultado.cStat}</span>
                </div>
                <p className="text-sm">{resultado.xMotivo}</p>
                {resultado.protocolo && <p className="text-xs text-muted-foreground mt-1">Protocolo: {resultado.protocolo}</p>}
              </div>
            )}
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Aba 2: Emitir em Massa ──────────────────────────────────────────────────

function AbaEmitirMassa({ isAdmin, emitentes }: { isAdmin: boolean; emitentes: any[] }) {
  const [emitenteId, setEmitenteId] = useState<number | null>(null);
  const [xmlFiles, setXmlFiles] = useState<{ name: string; content: string; chave?: string; numero?: string; destinatario?: string; erro?: string }[]>([]);
  const [xCorrecao, setXCorrecao] = useState("");
  const [resultados, setResultados] = useState<any[]>([]);
  const [emitindo, setEmitindo] = useState(false);
  const [downloadingMassaResult, setDownloadingMassaResult] = useState(false);
  const [downloadingResultId, setDownloadingResultId] = useState<number | null>(null);

  const utils = trpc.useUtils();

  const handleDownloadXmlResult = async (id: number) => {
    try {
      setDownloadingResultId(id);
      const result = await utils.cce.downloadXml.fetch({ id });
      downloadBase64Cce(result.base64, result.filename, "application/xml");
    } catch (err: any) {
      toast.error(`Erro ao baixar XML: ${err.message}`);
    } finally {
      setDownloadingResultId(null);
    }
  };

  const handleDownloadPdfResult = async (id: number) => {
    try {
      setDownloadingResultId(id);
      const result = await utils.cce.downloadPdf.fetch({ id });
      downloadBase64Cce(result.base64, result.filename, "application/pdf");
    } catch (err: any) {
      toast.error(`Erro ao baixar PDF: ${err.message}`);
    } finally {
      setDownloadingResultId(null);
    }
  };

  const handleDownloadXmlMassaResult = async () => {
    const ids = resultados.filter((r: any) => r.success && r.id).map((r: any) => r.id);
    if (ids.length === 0) return;
    try {
      setDownloadingMassaResult(true);
      const result = await utils.cce.downloadXmlMassa.fetch({ ids });
      downloadBase64Cce(result.base64, result.filename, "application/zip");
    } catch (err: any) {
      toast.error(`Erro ao baixar XMLs: ${err.message}`);
    } finally {
      setDownloadingMassaResult(false);
    }
  };

  const handleDownloadPdfMassaResult = async () => {
    const ids = resultados.filter((r: any) => r.success && r.id).map((r: any) => r.id);
    if (ids.length === 0) return;
    try {
      setDownloadingMassaResult(true);
      const result = await utils.cce.downloadPdfMassa.fetch({ ids });
      downloadBase64Cce(result.base64, result.filename, "application/zip");
    } catch (err: any) {
      toast.error(`Erro ao baixar PDFs: ${err.message}`);
    } finally {
      setDownloadingMassaResult(false);
    }
  };

  const emitirMassaMutation = trpc.cce.emitirMassa.useMutation({
    onSuccess: (data) => {
      setResultados(data);
      const ok = data.filter((r: any) => r.success).length;
      const err = data.filter((r: any) => !r.success).length;
      if (err === 0) toast.success(`${ok} CC-e(s) emitida(s) com sucesso!`);
      else toast.warning(`${ok} autorizada(s), ${err} com erro.`);
      utils.cce.historico.invalidate();
    },
    onError: (err) => {
      toast.error(`Erro: ${err.message}`);
    },
    onSettled: () => setEmitindo(false),
  });

  const parseXmlContent = (content: string, name: string) => {
    const chave = content.match(/<chNFe>(\d{44})<\/chNFe>/)?.[1] || content.match(/Id="NFe(\d{44})"/)?.[1];
    const numero = content.match(/<nNF>(\d+)<\/nNF>/)?.[1];
    const destinatario = content.match(/<xNome>([^<]+)<\/xNome>/g)?.[1]?.replace(/<\/?xNome>/g, "");
    return { name, content, chave, numero, destinatario };
  };

  const handleFiles = async (files: FileList | null) => {
    if (!files) return;
    const arr: typeof xmlFiles = [];
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      if (file.name.toLowerCase().endsWith(".zip")) {
        try {
          const zip = await JSZip.loadAsync(file);
          const xmlEntries = Object.values(zip.files).filter(
            (f) => !f.dir && f.name.toLowerCase().endsWith(".xml")
          );
          for (const entry of xmlEntries) {
            const content = await entry.async("string");
            arr.push(parseXmlContent(content, entry.name.split("/").pop() || entry.name));
          }
          toast.info(`${xmlEntries.length} XML(s) extraído(s) de ${file.name}`);
        } catch {
          toast.error(`Erro ao descompactar ${file.name}`);
        }
      } else {
        await new Promise<void>((resolve) => {
          const reader = new FileReader();
          reader.onload = (e) => {
            const content = e.target?.result as string;
            arr.push(parseXmlContent(content, file.name));
            resolve();
          };
          reader.readAsText(file);
        });
      }
    }
    if (arr.length > 0) setXmlFiles((prev) => [...prev, ...arr]);
  };

  const handleEmitir = () => {
    if (xmlFiles.length === 0) {
      toast.error("Adicione pelo menos um XML.");
      return;
    }
    if (xCorrecao.trim().length < 15) {
      toast.error("O texto de correção deve ter no mínimo 15 caracteres.");
      return;
    }
    // Não-admin: emitenteId é obrigatório (select aparece apenas para não-admin)
    if (!isAdmin && !emitenteId) {
      toast.error("Selecione um emitente.");
      return;
    }
    setEmitindo(true);
    setResultados([]);
    emitirMassaMutation.mutate({
      xmls: xmlFiles.map((f) => f.content),
      xCorrecao: xCorrecao.trim(),
      ...(emitenteId !== null ? { emitenteId } : {}),
    });
  };

  return (
    <div className="space-y-4">
      <AvisoLegal />

      <Card>
        <CardHeader>
          <CardTitle className="text-base">Configuração</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {!isAdmin && (
            <div>
              <Label className="mb-1 block">Emitente</Label>
              <Select
                value={emitenteId ? String(emitenteId) : ""}
                onValueChange={(v) => setEmitenteId(Number(v))}
              >
                <SelectTrigger className="w-72">
                  <SelectValue placeholder="Selecione o emitente" />
                </SelectTrigger>
                <SelectContent>
                  {emitentes.map((e: any) => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {e.nomeFantasia || e.razaoSocial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
          {isAdmin && (
            <p className="text-xs text-muted-foreground">Emitente detectado automaticamente pelo CNPJ de cada XML.</p>
          )}

          <div>
            <Label className="mb-1 block">Arquivos XML</Label>
            <div
              className="border-2 border-dashed rounded-lg p-6 text-center cursor-pointer hover:border-primary/50 transition-colors"
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files); }}
              onClick={() => document.getElementById("xml-upload")?.click()}
            >
              <Upload className="h-8 w-8 mx-auto mb-2 text-muted-foreground" />
              <p className="text-sm text-muted-foreground">Arraste XMLs ou um ZIP aqui, ou clique para selecionar</p>
              <p className="text-xs text-muted-foreground mt-1">Aceita .xml individuais ou um .zip contendo XMLs</p>
              <input id="xml-upload" type="file" accept=".xml,.zip" multiple className="hidden" onChange={(e) => handleFiles(e.target.files)} />
            </div>
          </div>

          {xmlFiles.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">Arquivo</th>
                    <th className="text-left p-3 font-medium">Chave</th>
                    <th className="text-left p-3 font-medium">Número</th>
                    <th className="text-left p-3 font-medium">Destinatário</th>
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {xmlFiles.map((f, i) => (
                    <tr key={i} className="border-b">
                      <td className="p-3 text-xs">{f.name}</td>
                      <td className="p-3 font-mono text-xs">{f.chave ? `${f.chave.substring(0, 20)}...` : <span className="text-red-500">Não encontrada</span>}</td>
                      <td className="p-3">{f.numero || "-"}</td>
                      <td className="p-3 text-xs">{f.destinatario || "-"}</td>
                      <td className="p-3">
                        <Button size="sm" variant="ghost" onClick={() => setXmlFiles((prev) => prev.filter((_, j) => j !== i))}>
                          <X className="h-3 w-3" />
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <div>
            <Label className="mb-1 block">Texto de Correção (aplicado a todos os XMLs)</Label>
            <Textarea
              placeholder="Descreva a correção (mínimo 15 caracteres)..."
              value={xCorrecao}
              onChange={(e) => setXCorrecao(e.target.value)}
              rows={4}
              maxLength={1000}
            />
            <p className={`text-xs mt-1 ${xCorrecao.length < 15 ? "text-red-500" : "text-muted-foreground"}`}>
              {xCorrecao.length}/1000 caracteres {xCorrecao.length < 15 && `(mínimo 15)`}
            </p>
          </div>

          <Button
            onClick={handleEmitir}
            disabled={emitindo || xmlFiles.length === 0 || xCorrecao.trim().length < 15 || (!isAdmin && !emitenteId)}
            className="w-full"
          >
            {emitindo ? "Emitindo..." : `Emitir CC-e para ${xmlFiles.length} nota(s)`}
          </Button>
        </CardContent>
      </Card>

      {resultados.length > 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">Resultado</CardTitle>
              {resultados.some((r: any) => r.success && r.id) && (
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={downloadingMassaResult} onClick={handleDownloadXmlMassaResult}>
                    <Download className="h-3 w-3 mr-1" />XMLs ({resultados.filter((r: any) => r.success && r.id).length})
                  </Button>
                  <Button size="sm" variant="outline" disabled={downloadingMassaResult} onClick={handleDownloadPdfMassaResult}>
                    <Download className="h-3 w-3 mr-1" />PDFs ({resultados.filter((r: any) => r.success && r.id).length})
                  </Button>
                </div>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-muted/50 border-b">
                  <tr>
                    <th className="text-left p-3 font-medium">Chave</th>
                    <th className="text-left p-3 font-medium">Status</th>
                    <th className="text-left p-3 font-medium">Seq.</th>
                    <th className="text-left p-3 font-medium">Protocolo</th>
                    <th className="text-left p-3 font-medium">cStat</th>
                    <th className="text-left p-3 font-medium">Mensagem</th>
                    <th className="text-left p-3 font-medium">Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {resultados.map((r: any, i: number) => (
                    <tr key={i} className="border-b">
                      <td className="p-3 font-mono text-xs">{r.chaveNfe ? `${r.chaveNfe.substring(0, 20)}...` : "-"}</td>
                      <td className="p-3"><StatusBadge status={r.success ? "autorizada" : r.erro ? "erro" : "rejeitada"} /></td>
                      <td className="p-3">{r.nSeqEvento ?? "-"}</td>
                      <td className="p-3 font-mono text-xs">{r.protocolo || "-"}</td>
                      <td className="p-3">{r.cStat || "-"}</td>
                      <td className="p-3 text-xs">{r.xMotivo || r.erro || "-"}</td>
                      <td className="p-3">
                        {r.success && r.id ? (
                          <div className="flex gap-1">
                            <Button size="sm" variant="ghost" disabled={downloadingResultId === r.id} onClick={() => handleDownloadXmlResult(r.id)} title="Baixar XML">
                              <FileText className="h-3 w-3" />
                            </Button>
                            <Button size="sm" variant="ghost" disabled={downloadingResultId === r.id} onClick={() => handleDownloadPdfResult(r.id)} title="Baixar PDF">
                              <Download className="h-3 w-3" />
                            </Button>
                          </div>
                        ) : "-"}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ─── Aba 3: Histórico ────────────────────────────────────────────────────────

function downloadBase64Cce(base64: string, filename: string, mimeType: string) {
  const byteChars = atob(base64);
  const byteArr = new Uint8Array(byteChars.length);
  for (let i = 0; i < byteChars.length; i++) byteArr[i] = byteChars.charCodeAt(i);
  const blob = new Blob([byteArr], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

function AbaHistorico({ isAdmin, emitentes }: { isAdmin: boolean; emitentes: any[] }) {
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("ALL");
  const [emitenteFilter, setEmitenteFilter] = useState<number | "ALL">("ALL");
  const [dataInicio, setDataInicio] = useState("");
  const [dataFim, setDataFim] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 20;
  const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set());
  const [downloadingId, setDownloadingId] = useState<number | null>(null);
  const [downloadingMassa, setDownloadingMassa] = useState(false);
  const [selectingAll, setSelectingAll] = useState(false);

  const { data, isLoading } = trpc.cce.historico.useQuery({
    search: search || undefined,
    status: statusFilter !== "ALL" ? statusFilter : undefined,
    emitenteId: emitenteFilter !== "ALL" ? emitenteFilter : undefined,
    dataInicio: dataInicio || undefined,
    dataFim: dataFim || undefined,
    page,
    pageSize,
  });

  const rows = data?.rows ?? [];
  const total = data?.total ?? 0;
  const totalPages = Math.ceil(total / pageSize);

  const utils = trpc.useUtils();

  const handleDownloadXml = async (id: number) => {
    try {
      setDownloadingId(id);
      const result = await utils.cce.downloadXml.fetch({ id });
      downloadBase64Cce(result.base64, result.filename, "application/xml");
    } catch (err: any) {
      toast.error(`Erro ao baixar XML: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadPdf = async (id: number) => {
    try {
      setDownloadingId(id);
      const result = await utils.cce.downloadPdf.fetch({ id });
      downloadBase64Cce(result.base64, result.filename, "application/pdf");
    } catch (err: any) {
      toast.error(`Erro ao baixar PDF: ${err.message}`);
    } finally {
      setDownloadingId(null);
    }
  };

  const handleDownloadXmlMassa = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      setDownloadingMassa(true);
      const result = await utils.cce.downloadXmlMassa.fetch({ ids });
      downloadBase64Cce(result.base64, result.filename, "application/zip");
    } catch (err: any) {
      toast.error(`Erro ao baixar XMLs: ${err.message}`);
    } finally {
      setDownloadingMassa(false);
    }
  };

  const handleDownloadPdfMassa = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    try {
      setDownloadingMassa(true);
      const result = await utils.cce.downloadPdfMassa.fetch({ ids });
      downloadBase64Cce(result.base64, result.filename, "application/zip");
    } catch (err: any) {
      toast.error(`Erro ao baixar PDFs: ${err.message}`);
    } finally {
      setDownloadingMassa(false);
    }
  };

  const toggleSelect = (id: number) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    const rowsComXml = rows.filter((r: any) => r.temXml);
    if (rowsComXml.every((r: any) => selectedIds.has(r.id))) {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        rowsComXml.forEach((r: any) => next.delete(r.id));
        return next;
      });
    } else {
      setSelectedIds((prev) => {
        const next = new Set(prev);
        rowsComXml.forEach((r: any) => next.add(r.id));
        return next;
      });
    }
  };

  const selectAllFilter = async () => {
    try {
      setSelectingAll(true);
      const ids = await utils.cce.listarIdsFiltro.fetch({
        search: search || undefined,
        status: statusFilter !== "ALL" ? statusFilter : undefined,
        emitenteId: emitenteFilter !== "ALL" ? emitenteFilter : undefined,
        dataInicio: dataInicio || undefined,
        dataFim: dataFim || undefined,
      });
      setSelectedIds(new Set(ids));
    } catch (err: any) {
      toast.error(`Erro ao selecionar todos: ${err.message}`);
    } finally {
      setSelectingAll(false);
    }
  };

  return (
    <div className="space-y-4">
      <Card>
        <CardContent className="pt-6">
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Buscar em todos os dados..."
                value={search}
                onChange={(e) => { setSearch(e.target.value); setPage(1); }}
                className="pl-9"
              />
            </div>

            {isAdmin && (
              <Select
                value={emitenteFilter === "ALL" ? "ALL" : String(emitenteFilter)}
                onValueChange={(v) => { setEmitenteFilter(v === "ALL" ? "ALL" : Number(v)); setPage(1); }}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Emitente" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="ALL">Todos os emitentes</SelectItem>
                  {emitentes.map((e: any) => (
                    <SelectItem key={e.id} value={String(e.id)}>
                      {e.nomeFantasia || e.razaoSocial}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}

            <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
              <SelectTrigger>
                <SelectValue placeholder="Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Todos os status</SelectItem>
                <SelectItem value="autorizada">Autorizada</SelectItem>
                <SelectItem value="rejeitada">Rejeitada</SelectItem>
                <SelectItem value="erro">Erro</SelectItem>
              </SelectContent>
            </Select>

            <Input type="date" value={dataInicio} onChange={(e) => { setDataInicio(e.target.value); setPage(1); }} placeholder="Data início" />
            <Input type="date" value={dataFim} onChange={(e) => { setDataFim(e.target.value); setPage(1); }} placeholder="Data fim" />
          </div>

          {(search || statusFilter !== "ALL" || emitenteFilter !== "ALL" || dataInicio || dataFim) && (
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setSearch(""); setStatusFilter("ALL"); setEmitenteFilter("ALL"); setDataInicio(""); setDataFim(""); setPage(1); }}
              className="mt-4"
            >
              Limpar filtros
            </Button>
          )}
        </CardContent>
      </Card>

      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-8 text-center text-muted-foreground">Carregando histórico...</div>
          ) : rows.length === 0 ? (
            <div className="p-8 text-center text-muted-foreground">
              <FileText className="h-12 w-12 mx-auto mb-4 opacity-50" />
              <p>Nenhum registro encontrado</p>
            </div>
          ) : (
            <>
              {selectedIds.size > 0 && (
                <div className="flex items-center gap-2 p-3 border-b bg-muted/30">
                  <span className="text-sm text-muted-foreground">{selectedIds.size} selecionado(s)</span>
                  <Button size="sm" variant="outline" disabled={downloadingMassa} onClick={handleDownloadXmlMassa}>
                    <Download className="h-4 w-4 mr-1" /> XML (.zip)
                  </Button>
                  <Button size="sm" variant="outline" disabled={downloadingMassa} onClick={handleDownloadPdfMassa}>
                    <Download className="h-4 w-4 mr-1" /> PDF (.zip)
                  </Button>
                  <Button size="sm" variant="ghost" onClick={() => setSelectedIds(new Set())}>
                    <X className="h-4 w-4 mr-1" /> Limpar seleção
                  </Button>
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-muted/50 border-b">
                    <tr>
                      <th className="p-3 w-8">
                        <input
                          type="checkbox"
                          checked={rows.filter((r: any) => r.temXml).length > 0 && rows.filter((r: any) => r.temXml).every((r: any) => selectedIds.has(r.id))}
                          onChange={toggleSelectAll}
                          title="Selecionar todos com XML"
                        />
                      </th>
                      <th className="text-left p-3 font-medium">Chave</th>
                      <th className="text-left p-3 font-medium">Número</th>
                      <th className="text-left p-3 font-medium">Seq.</th>
                      <th className="text-left p-3 font-medium">Status</th>
                      <th className="text-left p-3 font-medium">Protocolo</th>
                      <th className="text-left p-3 font-medium">cStat</th>
                      <th className="text-left p-3 font-medium">Mensagem</th>
                      <th className="text-left p-3 font-medium">Data</th>
                      {isAdmin && <th className="text-left p-3 font-medium">Emitente</th>}
                      <th className="text-center p-3 font-medium">XML</th>
                      <th className="text-center p-3 font-medium">PDF</th>
                    </tr>
                  </thead>
                  <tbody>
                    {rows.map((r: any) => (
                      <tr key={r.id} className={`border-b hover:bg-muted/30 ${selectedIds.has(r.id) ? "bg-muted/20" : ""}`}>
                        <td className="p-3">
                          {r.temXml ? (
                            <input type="checkbox" checked={selectedIds.has(r.id)} onChange={() => toggleSelect(r.id)} />
                          ) : null}
                        </td>
                        <td className="p-3 font-mono text-xs" title={r.chaveNfe}>{r.chaveNfe?.substring(0, 20)}...</td>
                        <td className="p-3">{r.nfeNumero || "-"}</td>
                        <td className="p-3">{r.nSeqEvento}</td>
                        <td className="p-3"><StatusBadge status={r.statusEmissao} /></td>
                        <td className="p-3 font-mono text-xs">{r.protocolo || "-"}</td>
                        <td className="p-3">{r.cStat || "-"}</td>
                        <td className="p-3 text-xs max-w-48 truncate" title={r.xMotivo}>{r.xMotivo || "-"}</td>
                        <td className="p-3 text-xs">{r.dhEvento ? new Date(r.dhEvento).toLocaleString("pt-BR") : "-"}</td>
                        {isAdmin && <td className="p-3 text-xs text-muted-foreground">{r.razaoSocialEmitente || "-"}</td>}
                        <td className="p-3 text-center">
                          {r.temXml ? (
                            <Button size="sm" variant="ghost" disabled={downloadingId === r.id} onClick={() => handleDownloadXml(r.id)}>
                              <Download className="h-4 w-4" />
                            </Button>
                          ) : "-"}
                        </td>
                        <td className="p-3 text-center">
                          <Button size="sm" variant="ghost" disabled={downloadingId === r.id} onClick={() => handleDownloadPdf(r.id)}>
                            <Download className="h-4 w-4" />
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              <div className="flex items-center justify-between p-4 border-t">
                <div className="flex items-center gap-3">
                  <p className="text-sm text-muted-foreground">{total} registro(s)</p>
                  <Button size="sm" variant="ghost" onClick={selectAllFilter} disabled={selectingAll} className="text-xs">
                    {selectingAll ? "Selecionando..." : `Selecionar todos do filtro (${total})`}
                  </Button>
                </div>
                {totalPages > 1 && (
                  <div className="flex gap-2">
                    <Button size="sm" variant="outline" disabled={page === 1} onClick={() => setPage((p) => p - 1)}>Anterior</Button>
                    <span className="text-sm self-center">{page} / {totalPages}</span>
                    <Button size="sm" variant="outline" disabled={page === totalPages} onClick={() => setPage((p) => p + 1)}>Próxima</Button>
                  </div>
                )}
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// ─── Página principal ────────────────────────────────────────────────────────

export default function CartaCorrecao() {
  const { user, loading: authLoading } = useGoogleAuth();
  const [activeTab, setActiveTab] = useState("individual");

  const isAdmin = user?.role === "admin";

  const { data: emitentes = [] } = trpc.emitentes.list.useQuery(undefined, { enabled: isAdmin });

  useEffect(() => {
    if (!authLoading && !user) {
      window.location.href = "/login";
    }
  }, [user, authLoading]);

  if (authLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto"></div>
          <p className="mt-4 text-muted-foreground">Carregando...</p>
        </div>
      </div>
    );
  }

  if (!user) return null;

  return (
    <div className="container mx-auto py-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold">Carta de Correção (CC-e)</h1>
        <p className="text-muted-foreground">Emita cartas de correção para NF-e autorizadas</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="individual">Emitir Individual</TabsTrigger>
          <TabsTrigger value="massa">Emitir em Massa</TabsTrigger>
          <TabsTrigger value="historico">Histórico</TabsTrigger>
        </TabsList>

        <TabsContent value="individual">
          <AbaEmitirIndividual isAdmin={isAdmin} emitentes={emitentes} />
        </TabsContent>

        <TabsContent value="massa">
          <AbaEmitirMassa isAdmin={isAdmin} emitentes={emitentes} />
        </TabsContent>

        <TabsContent value="historico">
          <AbaHistorico isAdmin={isAdmin} emitentes={emitentes} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
